import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { fetchClients } from "../../api/clientAPI";
import { placeholderImage, isValidUrl } from "../../components/invalidImage";

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getClients = async () => {
      try {
        const clientsData = await fetchClients();
        if (Array.isArray(clientsData)) {
          setClients(clientsData);
        } else {
          throw new Error("Clients data is not an array");
        }
      } catch (error) {
        setError(error.message);
      }
    };

    getClients();
  }, []);

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 4000,
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 0,
    cssEase: "linear",
    pauseOnHover: true,
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 4 } },
      { breakpoint: 1024, settings: { slidesToShow: 3 } },
      { breakpoint: 640, settings: { slidesToShow: 2 } },
      { breakpoint: 480, settings: { slidesToShow: 2 } },
    ],
  };

  return (
    <section
      id="ClientsPage"
      className="relative py-20 md:py-28 bg-slate-50 overflow-hidden"
    >
      {/* Decorative orbs */}
      <div className="absolute -top-10 left-1/4 w-72 h-72 bg-brand-100/60 rounded-full blur-3xl" />
      <div className="absolute -bottom-10 right-1/4 w-72 h-72 bg-eco-100/60 rounded-full blur-3xl" />

      <style>{`
        body { overflow-x: hidden; }
        .client-track .slick-track { display: flex; align-items: center; }
        .client-track .slick-slide > div { display: flex; align-items: center; justify-content: center; }
      `}</style>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-eco-50 text-eco-700 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-eco-500" />
            Trusted Partners
          </span>
          <h2 className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight mb-4">
            Klien <span className="text-gradient">Kami</span>
          </h2>
          <p className="font-overpass text-base lg:text-lg text-slate-600 leading-relaxed">
            Kami bangga menjadi mitra terpercaya bagi berbagai perusahaan dan
            organisasi di berbagai sektor industri.
          </p>
        </motion.div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          {/* Fade gradient on edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none" />

          {clients.length > 0 ? (
            <Slider {...settings} className="client-track">
              {clients.map((client) => (
                <div key={client.id} className="px-3">
                  <div className="group h-32 sm:h-36 bg-white rounded-2xl border border-slate-100 shadow-soft hover:shadow-medium card-hover flex items-center justify-center p-5 transition-all">
                    <img
                      src={
                        isValidUrl(client.image)
                          ? client.image
                          : placeholderImage
                      }
                      alt={client.name}
                      loading="lazy"
                      className="max-w-full max-h-full object-contain filter grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                    />
                  </div>
                </div>
              ))}
            </Slider>
          ) : (
            <div className="flex items-center justify-center gap-4 py-8">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-32 flex-1 rounded-2xl bg-slate-100 animate-pulse-soft"
                />
              ))}
            </div>
          )}

          {error && (
            <div className="text-center text-red-500 mt-6">{error}</div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ClientsPage;
